import { useState, useCallback, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/api/client';
import { useAuthStore } from '@/store/useAuthStore';

// ─── Typen ──────────────────────────────────────────────────────────────────

interface SearchResult {
  id: string;
  firstName: string;
  lastName: string | null;
  birthName: string | null;
  gender: string | null;
  birthDateText: string | null;
  birthPlace: string | null;
  deathDateText: string | null;
  deathPlace: string | null;
  occupation: string | null;
  isLiving: boolean;
  profileMedia: { id: string; filename: string } | null;
}

interface SearchResponse {
  data: SearchResult[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ─── Gender-Badge ───────────────────────────────────────────────────────────

const GENDER_LABELS: Record<string, string> = {
  male: 'M',
  female: 'W',
  other: 'D',
  unknown: '?',
};

const GENDER_COLORS: Record<string, string> = {
  male: 'bg-blue-100 text-blue-700',
  female: 'bg-pink-100 text-pink-700',
  other: 'bg-green-100 text-green-700',
  unknown: 'bg-slate-100 text-slate-600',
};

// ─── SearchPage ─────────────────────────────────────────────────────────────

export function SearchPage() {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [page, setPage] = useState(1);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);

  // Autofokus auf das Suchfeld
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Debounce: 300ms nach letztem Tastendruck
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  // API-Abfrage
  const { data: result, isLoading, isError } = useQuery<SearchResponse>({
    queryKey: ['search', debouncedQuery, page, dateFrom, dateTo],
    queryFn: async () => {
      const params: Record<string, string | number> = { q: debouncedQuery, page, limit: 20 };
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const { data } = await apiClient.get<SearchResponse>('/search', { params });
      return data;
    },
    enabled: debouncedQuery.length > 0,
    placeholderData: (prev) => prev,
  });

  const handlePersonClick = useCallback(
    (personId: string) => {
      navigate(`/?person=${personId}`);
    },
    [navigate],
  );

  return (
    <div className="flex h-screen flex-col bg-slate-50">
      {/* Topbar */}
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-slate-200 bg-white px-4 shadow-sm sm:gap-4">
        <Link
          to="/"
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
          </svg>
          <span className="hidden sm:inline">Zurück zum Stammbaum</span>
        </Link>
        <h1 className="text-base font-semibold text-slate-800 sm:text-lg">Personensuche</h1>
      </header>

      {/* Inhalt */}
      <main className="flex-1 overflow-y-auto p-4 sm:p-8">
        <div className="mx-auto w-full max-w-2xl">
          {/* Suchfeld */}
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name, Geburtsort, Beruf..."
              className="w-full rounded-lg border border-slate-300 bg-white py-3 pl-10 pr-4 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
            {/* Löschen-Button */}
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-slate-400 hover:text-slate-600"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Datumsfilter Toggle + Felder */}
          <div className="mt-2 flex items-center gap-2">
            <button
              onClick={() => setShowDateFilter(!showDateFilter)}
              className={`flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${
                showDateFilter || dateFrom || dateTo
                  ? 'border-blue-300 bg-blue-50 text-blue-700'
                  : 'border-slate-300 bg-white text-slate-500 hover:bg-slate-50'
              }`}
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
              </svg>
              Zeitraum
            </button>
            {(dateFrom || dateTo) && (
              <button
                onClick={() => { setDateFrom(''); setDateTo(''); }}
                className="text-xs text-slate-400 hover:text-slate-600"
              >
                Filter zurücksetzen
              </button>
            )}
          </div>

          {showDateFilter && (
            <div className="mt-2 flex flex-col gap-2 rounded-lg border border-slate-200 bg-white p-3 sm:flex-row sm:items-center">
              <div className="flex items-center gap-2">
                <label className="w-8 shrink-0 text-xs text-slate-500">Von:</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                  className="w-full rounded border border-slate-300 px-2 py-1 text-sm text-slate-700 focus:border-blue-400 focus:outline-none sm:w-auto"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="w-8 shrink-0 text-xs text-slate-500">Bis:</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                  className="w-full rounded border border-slate-300 px-2 py-1 text-sm text-slate-700 focus:border-blue-400 focus:outline-none sm:w-auto"
                />
              </div>
            </div>
          )}

          {/* Lade-Anzeige */}
          {isLoading && debouncedQuery && (
            <div className="mt-8 flex justify-center">
              <span className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
            </div>
          )}

          {/* Fehler */}
          {isError && (
            <div className="mt-8 rounded-lg border border-red-200 bg-red-50 p-4 text-center text-sm text-red-600">
              Suche fehlgeschlagen. Bitte versuche es erneut.
            </div>
          )}

          {/* Ergebnisse */}
          {result && debouncedQuery && (
            <div className="mt-6">
              <p className="mb-3 text-xs text-slate-400">
                {result.meta.total} {result.meta.total === 1 ? 'Ergebnis' : 'Ergebnisse'} für &ldquo;{debouncedQuery}&rdquo;
              </p>

              {result.data.length === 0 && (
                <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center">
                  <p className="text-sm text-slate-500">Keine Personen gefunden.</p>
                </div>
              )}

              {result.data.length > 0 && (
                <ul className="space-y-2">
                  {result.data.map((person) => {
                    const fullName = `${person.firstName} ${person.lastName ?? ''}`.trim();
                    const genderKey = person.gender ?? 'unknown';
                    const details: string[] = [];
                    if (person.birthDateText) details.push(`* ${person.birthDateText}`);
                    if (person.birthPlace) details.push(person.birthPlace);
                    if (person.occupation) details.push(person.occupation);

                    return (
                      <li key={person.id}>
                        <button
                          onClick={() => handlePersonClick(person.id)}
                          className="flex w-full items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 text-left transition-colors hover:border-blue-300 hover:bg-blue-50/50"
                        >
                          {/* Avatar */}
                          <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full border border-slate-200 bg-slate-100">
                            {person.profileMedia ? (
                              <img
                                src={accessToken ? `/uploads/${person.profileMedia.filename}` : undefined}
                                alt={fullName}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <svg className="mx-auto mt-2 h-6 w-6 text-slate-300" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                              </svg>
                            )}
                          </div>

                          {/* Info */}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="truncate text-sm font-semibold text-slate-900">{fullName}</span>
                              {person.birthName && (
                                <span className="truncate text-xs text-slate-400">geb. {person.birthName}</span>
                              )}
                              <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${GENDER_COLORS[genderKey] ?? GENDER_COLORS.unknown}`}>
                                {GENDER_LABELS[genderKey] ?? '?'}
                              </span>
                            </div>
                            {details.length > 0 && (
                              <p className="mt-0.5 truncate text-xs text-slate-500">
                                {details.join(' · ')}
                              </p>
                            )}
                          </div>

                          {/* Pfeil */}
                          <svg className="h-4 w-4 shrink-0 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                          </svg>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}

              {/* Paginierung */}
              {result.meta.totalPages > 1 && (
                <div className="mt-4 flex items-center justify-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                  >
                    Zurück
                  </button>
                  <span className="text-xs text-slate-500">
                    Seite {result.meta.page} von {result.meta.totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(result.meta.totalPages, p + 1))}
                    disabled={page >= result.meta.totalPages}
                    className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                  >
                    Weiter
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Leerer Zustand (vor Eingabe) */}
          {!debouncedQuery && (
            <div className="mt-8 rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center">
              <svg className="mx-auto h-10 w-10 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
              </svg>
              <p className="mt-3 text-sm font-medium text-slate-600">
                Personen durchsuchen
              </p>
              <p className="mt-1 text-xs text-slate-400">
                Suche nach Name, Geburtsname, Geburtsort, Beruf und mehr.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
